"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.toolDef = void 0;
exports.handle = handle;
const path = __importStar(require("path"));
const dist_1 = require("../../../fs/dist");
const serializeBundle_1 = require("../../../app/dist/context/serializeBundle");
const context_1 = require("../resources/context");
const INSTRUCTIONS = `Implement this step using your file-editing tools (Read, Edit, Write, Bash, etc.).
After implementation:
1. Call loom_append_done with { planId, stepNumber, notes } where "notes" summarizes what you actually did (files created/edited, decisions made).
2. Call loom_complete_step with { planId, stepNumber } to mark the step ✅.
If you reach a decision that needs human input, stop and ask — do not guess.`;
exports.toolDef = {
    name: 'loom_do_step',
    description: 'Get a brief for a step of a plan: thread context, step description, instructions. Pure read — no state changes, no AI inference. If stepNumber is omitted, returns the brief for the first not-done step. If provided, returns the brief for that exact step (errors if already done). When advancing through several steps of the same plan in one session, pass context: "skip" (or brief_only: true) on every call after the first so the ~6–7k-token thread bundle is not re-injected — you already hold it. The host agent (Claude Code, Cursor, etc.) is expected to act on the brief using its own tools, then call loom_append_done and loom_complete_step.',
    inputSchema: {
        type: 'object',
        properties: {
            planId: { type: 'string', description: 'Plan id. Canonical form is the ULID (e.g. "pl_01J…"); the filename stem (e.g. "my-weave-plan-001") is also accepted and resolved.' },
            stepNumber: { type: 'number', description: 'Optional. Specific step number to brief. If omitted, the first not-done step is used.' },
            context_ids: { type: 'array', items: { type: 'string' }, description: 'Optional. Additional doc IDs to inject into the brief context.' },
            context: { type: 'string', enum: ['skip'], description: 'Optional. Coarse "I hold the whole thread" shortcut: suppress the entire thread context bundle (~6–7k tokens). Use for the 2nd+ step of the same plan when nothing changed. For precise per-doc dedupe (e.g. after a refine bumped one doc) use alreadyLoaded instead.' },
            brief_only: { type: 'boolean', description: 'Optional. Alias for context: "skip". When true, the brief omits the thread context bundle and returns only the step description, files, summary, and instructions.' },
            alreadyLoaded: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { type: 'string', description: 'Doc id you already hold.' },
                        version: { type: 'number', description: 'The version you hold. A different current version re-injects the doc.' },
                    },
                    required: ['id', 'version'],
                },
                description: 'Optional. The context ledger (Context Dispatcher, model C): the {id, version} docs you already hold this session. The brief injects ONLY the delta (docs absent from the ledger or whose version changed) and reports the assumed-present rest in `contextManifest`. The precise form of context:"skip"; declaring every thread doc here ≡ skip. A version bump always re-injects, so there is no silent under-load.',
            },
        },
        required: ['planId'],
    },
};
async function handle(root, args) {
    const planId = args['planId'];
    const stepNumber = typeof args['stepNumber'] === 'number' ? args['stepNumber'] : undefined;
    const contextIds = Array.isArray(args['context_ids']) ? args['context_ids'] : [];
    const skipContext = args['context'] === 'skip' || args['brief_only'] === true;
    const alreadyLoaded = Array.isArray(args['alreadyLoaded'])
        ? args['alreadyLoaded'].filter(l => l && typeof l.id === 'string' && typeof l.version === 'number')
        : [];
    // Primary (agent-supplied) id → suggest-on-miss. The contextIds lookups below
    // stay on findDocumentById (best-effort enrichment, not the agent's target id).
    const { filePath: planFilePath } = await (0, dist_1.resolveDocIdOrThrow)(root, planId);
    const planDoc = await (0, dist_1.loadDoc)(planFilePath);
    if (planDoc.type !== 'plan')
        throw new Error(`Document ${planId} is not a plan`);
    if (planDoc.status !== 'implementing') {
        throw new Error(`Plan must be "implementing" to use DoStep. Current status: ${planDoc.status}`);
    }
    const allSteps = planDoc.steps ?? [];
    let nextStep;
    if (stepNumber !== undefined) {
        const target = allSteps.find(s => s.order === stepNumber);
        if (!target)
            throw new Error(`Step ${stepNumber} not found in plan ${planId}.`);
        if (target.status === 'done')
            throw new Error(`Step ${stepNumber} of plan ${planId} is already done.`);
        nextStep = target;
    }
    else {
        const pendingSteps = allSteps.filter(s => s.status !== 'done' && s.status !== 'cancelled');
        if (pendingSteps.length === 0)
            throw new Error('All steps are already done.');
        nextStep = pendingSteps[0];
    }
    // Derive weaveId/threadId from path: loom/{weaveId}/{threadId}/plans/{planId}.md
    const plansDir = path.dirname(planFilePath);
    const threadDir = path.dirname(plansDir);
    const weaveDir = path.dirname(threadDir);
    const threadId = path.basename(threadDir);
    const weaveId = path.basename(weaveDir);
    // Context Dispatcher (model C). Two dedupe controls, both routed through the one
    // injection door (the loom://context resource → assembleContext):
    //   - context:"skip" / brief_only — coarse: suppress the whole bundle (caller
    //     holds the entire thread). No resource call at all.
    //   - alreadyLoaded — precise: declare the {id, version} ledger; the assembler
    //     returns only the delta (docs absent or version-changed) + a manifest of
    //     what it assumed present. A version bump always re-injects (no silent
    //     under-load). Explicit context_ids stay additive — the inverse of the ledger.
    let threadContext = '';
    let contextManifest = [];
    if (skipContext) {
        threadContext = '(thread context suppressed — caller declared it already loaded this session; omit the context/brief_only flag to re-inject the full bundle)';
    }
    else {
        try {
            const loadedQuery = alreadyLoaded.length
                ? `&loaded=${alreadyLoaded.map(l => `${encodeURIComponent(l.id)}@${l.version}`).join(',')}`
                : '';
            const ctx = await (0, context_1.handleContextResource)(root, `loom://context/${planId}?mode=implementing&format=json${loadedQuery}`);
            const bundle = JSON.parse(ctx.contents[0].text);
            threadContext = (0, serializeBundle_1.serializeBundle)(bundle);
            contextManifest = bundle.manifest ?? [];
        }
        catch { /* proceed without ctx if unavailable */ }
    }
    if (contextIds.length > 0) {
        const extraParts = await Promise.all(contextIds.map(async (id) => {
            try {
                const fp = await (0, dist_1.findDocumentById)(root, id);
                if (!fp)
                    return null;
                const doc = await (0, dist_1.loadDoc)(fp);
                return `### ${doc.title} (${doc.type})\n\n${doc.content ?? ''}`;
            }
            catch {
                return null;
            }
        }));
        const extra = extraParts.filter(Boolean).join('\n\n---\n\n');
        if (extra)
            threadContext += `\n\n## Additional Context\n\n${extra}`;
    }
    const planSummary = (planDoc.steps ?? [])
        .map(s => `${s.status === 'done' ? '✅' : '⬜'} Step ${s.order}: ${s.description}`)
        .join('\n');
    const brief = {
        planId,
        planTitle: planDoc.title,
        weaveId,
        threadId,
        stepNumber: nextStep.order,
        stepDescription: nextStep.description,
        filesToTouch: nextStep.files_touched,
        contextSkipped: skipContext,
        contextManifest,
        threadContext,
        planSummary,
        instructions: INSTRUCTIONS,
    };
    return {
        content: [{
                type: 'text',
                text: JSON.stringify(brief, null, 2),
            }],
    };
}
